import { useEffect, useState } from 'react';
import { GetStaticProps } from 'next';

import {
  ComponentInsights,
  DynamicComponents,
  PageHead,
  Schema,
  isArrayMatch,
} from '@/ui';
import { getInsightsListingStaticProps } from '@/intergrations';

import { useRouter } from 'next/router';
import { FilterTag } from '@/types';

export const getStaticProps: GetStaticProps = async (context) => {
  return await getInsightsListingStaticProps(context);
};

export const InsightsListing = (props: {
  baseData: any;
  pageData: any;
  listingItems: any;
  insightTypes: string[];
  pageTags: string[];
  currentPage: string;
  totalPages: string;
  totalItems: number;
  insightPerPage: string;
}) => {
  const {
    baseData,
    pageData,
    listingItems,
    insightTypes,
    pageTags,
    currentPage,
    totalPages,
    totalItems,
    insightPerPage,
  } = props;

  const { locale = 'en' } = useRouter();

  const [loadStatus, setLoadStatus] = useState('All');
  
  // Initialize with props, but will be updated by the first API call
  const [availableInsightTypes, setAvailableInsightTypes] = useState<string[]>(insightTypes);
  
  // Initialize filters with the full set of insight types
  const [filters, setFilters] = useState({
    page: currentPage,
    insightTypes: insightTypes,
    pageTags: [],
  });

  const [filtersApplied, setFiltersApplied] = useState(false);

  // Initialize listing data state
  const [listingData, setListingData] = useState<{
    listingItems: any;
    insightTypes: string | string[] | undefined;
    pageTags: string[];
    currentPage: string;
    totalPages: string;
    totalItems: number;
    insightPerPage: string;
  }>({
    listingItems,
    insightTypes,
    pageTags,
    currentPage,
    totalPages,
    totalItems,
    insightPerPage,
  });

  // Add immediate effect to fetch all insight types as soon as the component mounts
  useEffect(() => {
    // Set immediate loading state
    setLoadStatus('loading');
    
    const fetchAllInsightTypes = async () => {
      try {
        // Make a request to the API without filters to get all possible insight types
        const response = await fetch(`/api/insights/${locale}/1`);
        const data = await response.json();
        
        if (data.insightTypes && typeof data.insightTypes === 'string') {
          const apiInsightTypes = data.insightTypes.split(',');
          
          // Create a comprehensive list of all insight types
          const mergedTypes = [...new Set([...insightTypes, ...apiInsightTypes])];          
          
          // Update both the available types AND update the filters to use ALL types
          setAvailableInsightTypes(mergedTypes);
          
          // Update the filters to include all insight types
          setFilters(prev => ({
            ...prev,
            insightTypes: mergedTypes
          }));
          
          // Also update the listing data with the first page of results
          setListingData(data);
        }
      } catch (error) {
        console.error('Error fetching all insight types:', error);
      } finally {
        setLoadStatus('done');
      }
    };

    // Execute this immediately on component mount
    fetchAllInsightTypes();
  }, [locale]); // Only run on locale change, not on insightTypes change to avoid loops

  const handleChangeFilters = (
    newFilters: string | string[],
    newTags: Array<FilterTag>,
    newPage?: string
  ) => {
    if (newFilters === 'All') {
      newFilters = availableInsightTypes;
      setLoadStatus('onAll');
    }
    setFilters((prev: any) => {
      return {
        page: newPage ? newPage : prev.page,
        pageTags: newTags ? newTags.map((t: FilterTag) => t.id) : prev.pageTags,
        insightTypes: newFilters ? [newFilters] : prev.insightTypes,
      };
    });
  };

  useEffect(() => {
    const callApi = async () => {
      setLoadStatus('filtering');

      const apiCallParams = `${locale}/${
        filters.page
      }/${filters.insightTypes.join(',')}/${filters.pageTags.join(',')}`;

      try {
        const response = await fetch(`/api/insights/${apiCallParams}`);
        const data = await response.json();
        setListingData(data);
        
        // Extract and update available insight types from API response
        if (data.insightTypes && typeof data.insightTypes === 'string') {
          const apiInsightTypes = data.insightTypes.split(',');
          // Update availableInsightTypes if needed
          if (JSON.stringify([...new Set([...availableInsightTypes, ...apiInsightTypes])]) 
              !== JSON.stringify(availableInsightTypes)) {
            const updatedTypes = [...new Set([...availableInsightTypes, ...apiInsightTypes])];
            setAvailableInsightTypes(updatedTypes);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadStatus('done');
      }
    };

    callApi().catch((error) => console.error('Error calling API:', error));
  }, [filters, locale]);

  useEffect(() => {
    if (loadStatus == 'done' || loadStatus == 'onAll') {
      if (Array.isArray(filters.insightTypes[0])) {
        setFiltersApplied(
          !(
            isArrayMatch(filters.insightTypes[0], availableInsightTypes) &&
            (filters.pageTags.length === 0 || listingData.pageTags.length === 0)
          )
        );
      } else {
        setFiltersApplied(
          !(
            isArrayMatch(filters.insightTypes, availableInsightTypes) &&
            (filters.pageTags.length === 0 || listingData.pageTags.length === 0)
          )
        );
      }
    }
  }, [
    listingData,
    loadStatus,
    availableInsightTypes,
    filters
  ]);

  const topComponents = pageData?.pageTopCuratedArea ?? [];
  const bottomComponents = pageData?.pageBottomCuratedArea ?? [];
  const insightsComponents = pageData?.insightsComponents ?? [];
  const mergedData = { ...baseData, ...pageData };

  return (
    <>
      <PageHead pageData={mergedData} />
      <Schema pageData={baseData} />
      <DynamicComponents components={topComponents} />
      <ComponentInsights
        pageData={pageData}
        listingItems={listingData.listingItems}
        insightTypes={availableInsightTypes}
        pageTags={listingData.pageTags}
        currentPage={listingData.currentPage}
        totalPages={listingData.totalPages}
        totalItems={listingData.totalItems}
        insightPerPage={listingData.insightPerPage}
        onChangeFilters={handleChangeFilters}
        insightsComponents={insightsComponents}
        filtersApplied={filtersApplied}
        loadStatus={loadStatus}
      />
      <DynamicComponents components={bottomComponents} />
    </>
  );
};

export default InsightsListing;
